// Copyright © 2025 The Things Industries B.V.

import React from 'react'
import { defineMessages, FormattedNumber } from 'react-intl'
import classnames from 'classnames'
import { useSelector } from 'react-redux'

import STORAGE_INTEGRATION from '@console/constants/storage-integration'

import Icon, { IconAlertCircle, IconDatabase } from '@ttn-lw/components/icon'
import Panel, { PanelError } from '@ttn-lw/components/panel'
import Link from '@ttn-lw/components/link'
import Tooltip from '@ttn-lw/components/tooltip'
import StatusLabel from '@ttn-lw/components/status-label'

import Message from '@ttn-lw/lib/components/message'
import RequireRequest from '@ttn-lw/lib/components/require-request'

import sharedMessages from '@ttn-lw/lib/shared-messages'
import PropTypes from '@ttn-lw/lib/prop-types'

import { getStoredApplicationUplinkMessagesCount } from '@console/store/actions/application-server'
import { getAppPkgDefaultAssoc } from '@console/store/actions/application-packages'

import { selectApplicationPackageDefaultAssociation } from '@console/store/selectors/application-packages'
import { selectAppStoredMessagesCount } from '@console/store/selectors/application-server.tti'

import style from './last-stored-messages-panel.tti.styl'

const m = defineMessages({
  lastStoredMessages: 'Last stored messages',
  retrieveData:
    'Retrieve data from any entity and download the report in <Link>Message storage</Link>.',
  messageStorageActivated: 'Message storage activated <Icon></Icon>',
  messageStorageDeactivated: 'Message storage deactivated <Icon></Icon>',
  messageStorageUnavailable: 'Message storage is currently unavailable',
  messagesStored: 'Messages stored',
  lastFifteenDays: 'In the last 15 days',
  activatedTooltip:
    'You are currently storing messages from this application into our servers. Deactivate it in the dedicated message storage page.',
  deactivatedTooltip:
    'You are currently not storing messages from this application into our servers. Activate it in the dedicated message storage page.',
})

const LastStoredMessagesPanel = ({ appId, className }) => {
  const currentPackage = useSelector(state =>
    selectApplicationPackageDefaultAssociation(state, STORAGE_INTEGRATION.DEFAULT_PORT),
  )
  const totalStoredMessages = useSelector(selectAppStoredMessagesCount)
  const isActivated = Boolean(currentPackage)

  return (
    <Panel
      title={m.lastStoredMessages}
      icon={IconDatabase}
      shortCutLinkTitle={sharedMessages.messageStorage}
      shortCutLinkPath={`/applications/${appId}/message-storage`}
      className={classnames(style.panel, className)}
    >
      <RequireRequest
        spinnerProps={{ inline: true, center: true }}
        errorRenderFunction={() => (
          <PanelError>
            <Message
              component="p"
              content={m.messageStorageUnavailable}
              className="c-text-neutral-light text-center"
            />
          </PanelError>
        )}
        requestAction={getAppPkgDefaultAssoc(appId, STORAGE_INTEGRATION.DEFAULT_PORT, ['data'])}
      >
        <div className={style.container}>
          <RequireRequest
            spinnerProps={{ inline: true, center: true }}
            requestAction={getStoredApplicationUplinkMessagesCount(appId, '360h')}
          >
            <StatusLabel
              className={style.status}
              type={isActivated ? 'success' : 'warning'}
              content={isActivated ? m.messageStorageActivated : m.messageStorageDeactivated}
              contentValues={{
                Icon: () => (
                  <Tooltip
                    content={
                      <Message
                        content={isActivated ? m.activatedTooltip : m.deactivatedTooltip}
                        values={{
                          Link: val => (
                            <Link secondary to={`/applications/${appId}/message-storage`}>
                              {val}
                            </Link>
                          ),
                        }}
                      />
                    }
                    small
                  >
                    <Icon
                      icon={IconAlertCircle}
                      className={isActivated ? 'c-text-success-bold' : 'c-text-warning-bold'}
                      small
                    />
                  </Tooltip>
                ),
              }}
            />

            <div className="d-flex al-center j-center flex-wrap">
              <span className="c-text-neutral-heavy fw-bold fs-xl4 m-sides-cs-l">
                <FormattedNumber value={totalStoredMessages} />
              </span>
              <div>
                <Message
                  content={m.messagesStored}
                  className={classnames(style.messagesStored, 'fw-bold fs-xl m-0')}
                  component="p"
                />
                <Message
                  content={m.lastFifteenDays}
                  className="c-text-neutral-light m-0"
                  component="p"
                />
              </div>
            </div>
          </RequireRequest>
        </div>
        <Message
          className="mt-cs-m c-text-neutral-semilight"
          content={m.retrieveData}
          values={{
            Link: val => (
              <Link primary to={`/applications/${appId}/message-storage`}>
                {val}
              </Link>
            ),
          }}
        />
      </RequireRequest>
    </Panel>
  )
}

LastStoredMessagesPanel.propTypes = {
  appId: PropTypes.string.isRequired,
  className: PropTypes.string,
}

LastStoredMessagesPanel.defaultProps = {
  className: undefined,
}

export default LastStoredMessagesPanel
